// src/store/eventsStore.js
import { defineStore } from 'pinia';
import { formatDuration, format } from 'date-fns';
import { getDefaultSelectedDate } from '../helpers/getDefaultSelectedDate';
import { ApiError } from '@sharedtypes/apiRoutes';
import type { AvlEvent, RegenHourFuel as RegenDuration, SummaryItems } from '@sharedtypes/models';
import { handleError } from '@modules/utils/errorHandler';
import processOutOfRegen from '@modules/eventStreamCorrection';
import ClientAppError from '../modules/utils/clientAppError';
import { getDeviceEventsByDate, getFuelRegen } from '../modules/apiService';
import AVL_EVENT_TYPES from '../helpers/eventTypes';
import { regenTotalDuration } from '../modules/fuelRegenCalc';
import { useDeviceStore } from './deviceStore';

function eventTypeCounter(events: AvlEvent[]): SummaryItems {
  let ignitionOn = 0;
  let ignitionOff = 0;
  let regenOn = 0;
  let regenOff = 0;
  let fuelOn = 0;

  events.forEach((event) => {
    switch (event.eventType) {
      case 'Ignition On':
        ignitionOn += 1;
        break;
      case 'Ignition Off':
        ignitionOff += 1;
        break;
      case 'ReGen On':
        regenOn += 1;
        break;
      case 'ReGen Off':
        regenOff += 1;
        break;
      case 'Fuel On':
        fuelOn += 1;
        break;
      default:
        break;
    }
  });

  return {
    Ignition: ignitionOn,
    Regens: regenOn,
    'Fuel On': fuelOn,
    _ignitionMismatch: ignitionOn !== ignitionOff ? 'true' : 'false',
    _regenMismatch: regenOn !== regenOff ? 'true' : 'false',
  };
}

/**
 * Structure of store's state.
 */
type EventsState = {
  events: AvlEvent[];
  regenHours: RegenDuration[];
  selectedDeviceId: number | null;
  selectedDate: Date | null;
  summaryData: SummaryItems;
  isLoaded: boolean;
  hasData: boolean;
  isLoading: boolean;
  isFuelRegenLoading: boolean;
};

 
export const useEventsStore = defineStore('events', {
  state: (): EventsState => ({
    /** @type {AvlEvent[]} */
    events: [],
    /** @type {RegenDuration[]} */
    regenHours: [],
    selectedDeviceId: null,
    selectedDate: getDefaultSelectedDate(),
    // @type {SummaryItems}
    summaryData: {},
    isLoaded: false,
    hasData: false,
    isLoading: false,
    isFuelRegenLoading: false,
  }),
  actions: {
    /**
     * Fetches events for the selected device and date.
     * @returns {Promise<void>} - A promise that resolves when the events are fetched.
     */
    async fetchEvents(): Promise<void> {
      this.isLoaded = false;
      this.isLoading = true;
      this.hasData = false;
      try {
        if (this.selectedDate === null || this.selectedDeviceId === null) {
          throw ClientAppError.fromError(new Error('Device ID and Date are required'));
        }
        const [response] = await Promise.all([
          getDeviceEventsByDate(
            this.selectedDeviceId,
            this.getFormattedSelectedDate,
          ),
          //  getAllEvents(this.selectedDeviceId, this.getFormattedSelectedDate),
          this.fetchRegenHours(this.selectedDeviceId),
        ]);

        const filteredEvents = response.events.filter(
          (event) => {
            if (event.eventType === null) return false;
            return Object.values(AVL_EVENT_TYPES).includes(event.eventType);
          },
        );
        // change event time to be date object

        this.events = processOutOfRegen(filteredEvents);

        if (this.events.length > 0) {
          this.hasData = true;
        }
      } catch (error) {
        handleError(error, 'eventsStore.fetchEvents');
      } finally {
        this.isLoaded = true;
        this.isLoading = false;
      }
    },
    /**
     *
     * @param {number} deviceId - Device ID
     * @returns {Promise<void>}
     */
    async fetchRegenHours(deviceId: number): Promise<void> {
      this.isFuelRegenLoading = true;
      try {
        const deviceStore:ReturnType<typeof useDeviceStore> = useDeviceStore();

        const response = await getFuelRegen(deviceId);
        if (response instanceof ApiError) {
          throw response;
        }
        const fuelMultiplier = deviceStore.getFuelMultiplier(deviceId);
        if (fuelMultiplier === null) {
          throw ClientAppError.fromError(new Error(`No valid Fuel Multiplier found for ${deviceId}`));
        }
        response.forEach((regenStat) => {
           
          regenStat.fuelUsed = regenStat.fuelEventCount * fuelMultiplier;
        });

        this.regenHours = response;
      } catch (error) {
        handleError(error, 'eventsStore.fetchRegenHours');
      } finally {
        this.isFuelRegenLoading = false;
      }
    },
    /**
     *
     * @param {number} deviceId - Device ID
     */
    setSelectedDeviceId(deviceId:number):void {
      this.selectedDeviceId = deviceId;
      // this.fetchRegenHours(deviceId);
    },
    /**
     * @param {string | Date} date - Date string or Date object
     */
    setSelectedDate(date:string|Date):void {
      // if date is a string and valid Date
      if (typeof date === 'string' && new Date(date).toString() !== 'Invalid Date') {
        const [year, month, day] = date.split('-').map(Number);
        this.selectedDate = new Date(year, month - 1, day);
        this.selectedDate.setHours(0, 0, 0, 0);
      } else if (date instanceof Date) {
        this.selectedDate = date;
      } else {
        throw ClientAppError.fromError(new Error('eventStore.setSelectedDate Invalid Date'));
      }
    },
  },
  getters: {
    getEvents():AvlEvent[] {
      return this.events;
    },
    fuelEvents(state):AvlEvent[] {
      return state.events.filter((event) => event.eventType === AVL_EVENT_TYPES.FUELON);
    },
    fuelMultiplier(): number {
      const deviceStore = useDeviceStore();

      // get current device id and get device details of that device from device store
      // return the fuel multiplier from the device details
      if (this.selectedDeviceId === null) throw ClientAppError.fromError(new Error('eventStore.fuelMultiplier Selected Device ID is required'));
      const fuelMultiplier = deviceStore.getFuelMultiplier(this.selectedDeviceId);
      if (fuelMultiplier === null) throw ClientAppError.fromError(new Error('eventStore.fuelMultiplier Fuel Multiplier not found'));

      return fuelMultiplier;
    },
    getEventsSummary(): SummaryItems {
      const summaryData = eventTypeCounter(this.events);
      // handle error thrown by regenTotalDuration
      let regenDurationFormatted = 'N/A';

      const regenResult = regenTotalDuration(this.events);
      if (regenResult.error) {
        handleError(regenResult.error, 'eventsStore.getEventsSummary');
      } else if (typeof regenResult.totalDurationInSec === 'number') {
        const regenDuration = regenResult.totalDurationInSec;
        const duration = {
          hours: Math.floor(regenDuration / 3600),
          minutes: Math.floor((regenDuration % 3600) / 60),
        };
        regenDurationFormatted = formatDuration(duration);
      }

      try {
        summaryData['Fuel Used'] = `${Number(summaryData['Fuel On']) * Number(this.fuelMultiplier)} gals`;
        summaryData['Regen Duration'] = regenDurationFormatted;
        delete summaryData['Fuel On'];
        // Optionally, you can expose the mismatch variables for UI later
        // summaryData['_ignitionMismatch'] and summaryData['_regenMismatch']
      } catch (error) {
        handleError(error, 'eventsStore.getEventsSummary');
      }
      return summaryData;
    },
    getSelectedDate(): Date {
      if (this.selectedDate === null) throw ClientAppError.fromError(new Error('eventStore.getSelectedDate Selected Date is required'));
      // create a new date with the date from the field and set the time to 00:00:00
      return new Date(this.selectedDate);
    },

    getFormattedSelectedDate(): string {
      if (this.selectedDate === null) throw ClientAppError.fromError(new Error('eventStore.getFormattedSelectedDate Selected Date is required'));

      return format(this.selectedDate, 'yyyy-MM-dd');
    },
    getRegenHoursForSelectedDate():RegenDuration[] {
      if (this.selectedDate === null) throw ClientAppError.fromError(new Error('eventStore.getRegenHoursForSelectedDate Selected Date is required'));
      if (this.regenHours.length === 0) {
        handleError(
          {
            message:
        `No regen hours found for ${this.selectedDeviceId} on ${this.selectedDate}`,
          },
          'eventStore.getRegenHoursForSelectedDate',
        );
        return [];
      }

      const selectedDateStart = new Date(this.selectedDate);
      selectedDateStart.setHours(0, 0, 0, 0);
      const selectedDateEnd = new Date(this.selectedDate);
      selectedDateEnd.setHours(23, 59, 59, 999);
      const regenHoursForSelectedDate = this.regenHours.filter(
        /**
         * @returns {boolean} - If the regen hour is within the selected date
         */
        (regen:RegenDuration):boolean => {
          const regenEnd = new Date(regen.regenHourEnd);
          return regenEnd >= selectedDateStart && regenEnd <= selectedDateEnd;
        },
      );
      return regenHoursForSelectedDate;
    },
  },
});
